import { useMemo, useRef, useState } from "react";
import { Link as RouterLink, useNavigate } from "react-router-dom";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import Popover from "@mui/material/Popover";
import InputBase from "@mui/material/InputBase";
import List from "@mui/material/List";
import ListItemButton from "@mui/material/ListItemButton";
import ListItemText from "@mui/material/ListItemText";
import Typography from "@mui/material/Typography";
import Divider from "@mui/material/Divider";
import CircularProgress from "@mui/material/CircularProgress";
import { ChevronDown, Search, ArrowRight } from "lucide-react";
import { useRecentEvents } from "../hooks/useRecentEvents";
import type { Event } from "@event-planner/shared";

interface EventSwitcherProps {
  /** Currently active event (derived from route). Null if not inside an event. */
  currentEvent: Event | null;
  /** Controlled open state — lifted so scoped sidebar items can pop the switcher. */
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /**
   * Path suffix appended to `/events/{eventId}` on selection. Set by the
   * Sidebar when the user clicks an event-scoped item (e.g. "/attendees")
   * while no event is selected — so after picking an event the user lands
   * on the page they originally intended, not just the overview.
   */
  pendingPath?: string | null;
}

/**
 * Event context switcher. Sits at the top of the primary sidebar. Lets the
 * user jump between events without first navigating to `/events`. Also acts
 * as a hint target when a user clicks an event-scoped sidebar item with no
 * event selected — the Sidebar opens this popover instead of navigating.
 */
export function EventSwitcher({ currentEvent, open, onOpenChange, pendingPath }: EventSwitcherProps) {
  const anchorRef = useRef<HTMLButtonElement>(null);
  const navigate = useNavigate();
  const { data: recentEvents, loading } = useRecentEvents(5);
  const [searchTerm, setSearchTerm] = useState("");

  const filtered = useMemo(() => {
    const term = searchTerm.trim().toLowerCase();
    if (!term) return recentEvents;
    return recentEvents.filter((e) => e.name.toLowerCase().includes(term));
  }, [recentEvents, searchTerm]);

  function handleSelect(event: Event) {
    onOpenChange(false);
    setSearchTerm("");
    navigate(`/events/${event.id}${pendingPath || ""}`);
  }

  return (
    <>
      <Box sx={{ px: 1.5, py: 1 }}>
        <Button
          ref={anchorRef}
          fullWidth
          variant="outlined"
          color="inherit"
          onClick={() => onOpenChange(!open)}
          endIcon={<ChevronDown size={16} />}
          sx={{
            justifyContent: "space-between",
            textTransform: "none",
            borderColor: "divider",
            color: currentEvent ? "text.primary" : "text.secondary",
            "& .MuiButton-endIcon": { ml: 1 },
          }}
          aria-haspopup="listbox"
          aria-expanded={open}
          aria-label={
            currentEvent ? `Switch event (current: ${currentEvent.name})` : "Select event"
          }
        >
          <Typography variant="body2" noWrap sx={{ flexGrow: 1, textAlign: "left" }}>
            {currentEvent ? currentEvent.name : "Select event…"}
          </Typography>
        </Button>
      </Box>
      <Popover
        open={open}
        anchorEl={anchorRef.current}
        onClose={() => onOpenChange(false)}
        anchorOrigin={{ vertical: "bottom", horizontal: "left" }}
        transformOrigin={{ vertical: "top", horizontal: "left" }}
        slotProps={{
          paper: {
            sx: { width: 280, mt: 0.5 },
          },
        }}
      >
        <Box sx={{ display: "flex", alignItems: "center", gap: 1, px: 1.5, py: 1 }}>
          <Search size={16} />
          <InputBase
            // eslint-disable-next-line jsx-a11y/no-autofocus -- intentional: dropdown search field should capture focus on open
            autoFocus
            fullWidth
            placeholder="Search events…"
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            inputProps={{ "aria-label": "Search events" }}
            sx={{ fontSize: 14 }}
          />
        </Box>
        <Divider />
        <Box sx={{ px: 1.5, pt: 1, pb: 0.5 }}>
          <Typography variant="caption" color="text.secondary">
            Recent
          </Typography>
        </Box>
        {loading ? (
          <Box sx={{ display: "flex", justifyContent: "center", py: 2 }}>
            <CircularProgress size={20} />
          </Box>
        ) : filtered.length === 0 ? (
          <Typography variant="body2" color="text.secondary" sx={{ px: 1.5, py: 1 }}>
            {searchTerm ? "No matches." : "No recent events."}
          </Typography>
        ) : (
          <List dense disablePadding>
            {filtered.map((event) => (
              <ListItemButton
                key={event.id}
                onClick={() => handleSelect(event)}
                selected={currentEvent?.id === event.id}
              >
                <ListItemText
                  primary={event.name}
                  secondary={event.startDate}
                  primaryTypographyProps={{ variant: "body2", noWrap: true }}
                  secondaryTypographyProps={{ variant: "caption" }}
                />
              </ListItemButton>
            ))}
          </List>
        )}
        <Divider />
        <ListItemButton
          component={RouterLink}
          to="/events"
          onClick={() => onOpenChange(false)}
          sx={{ justifyContent: "space-between" }}
        >
          <Typography variant="body2">View all events</Typography>
          <ArrowRight size={16} />
        </ListItemButton>
      </Popover>
    </>
  );
}
